import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import { compose } from 'ramda';
import { withProviders } from 'src/common/hocs/with-providers';
import type { RootState, AppDispatch } from 'src/redux';

import { PageTitleSection } from '../common/components/page-title-section/page-title-section';
import { PublicPageLayout } from '../common/components/public-page-layout/public-page-layout';
import './blog.css';
import { BlogOverview } from './components/blog-overview/blog-overview';
import { LoadMore } from './components/load-more/load-more';
import { fetchBlogPosts } from './state/blog-actions';
import {
  selectCurrentPage,
  selectHasMore,
  selectIsInitialFetchDone,
  selectIsLoading,
  selectPosts,
} from './state/blog-selectors';

type BlogComponentProps = {
  fetchBlogPosts: (currentPage: number) => void;
  currentPage: ReturnType<typeof selectCurrentPage>;
  hasMore: ReturnType<typeof selectHasMore>;
  isInitialFetchDone: ReturnType<typeof selectIsInitialFetchDone>;
  isLoading: ReturnType<typeof selectIsLoading>;
  posts: ReturnType<typeof selectPosts>;
};

const BlogComponent = ({
  currentPage,
  hasMore,
  isInitialFetchDone,
  isLoading,
  posts,
  fetchBlogPosts,
}: BlogComponentProps) => {
  useEffect(() => {
    if (currentPage === 1 && !isInitialFetchDone) {
      fetchBlogPosts(currentPage);
    }
  }, [fetchBlogPosts, currentPage, isInitialFetchDone]);

  const onLoadMoreClick = useCallback(() => {
    fetchBlogPosts(currentPage);
  }, [fetchBlogPosts, currentPage]);

  return (
    <PublicPageLayout variant="navy">
      <Helmet>
        <title>{window._STM_PREDATA.blog.title}</title>
        <meta
          name="description"
          content={window._STM_PREDATA.blog.description}
        ></meta>
        <link rel="canonical" href="https://streamable.com/blog" />
      </Helmet>

      <PageTitleSection
        title="Streamable blog"
        introduction="Articles on video production, video hosting, video distribution, and more"
        showCTA={false}
      />

      <BlogOverview isLoading={isLoading} posts={posts} />

      <LoadMore
        hasMore={hasMore}
        isLoading={isLoading}
        onClick={onLoadMoreClick}
      />
    </PublicPageLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentPage: selectCurrentPage(state),
  hasMore: selectHasMore(state),
  isInitialFetchDone: selectIsInitialFetchDone(state),
  isLoading: selectIsLoading(state),
  posts: selectPosts(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchBlogPosts: (currentPage: number) =>
    dispatch(fetchBlogPosts(currentPage)),
});

export const Blog = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProviders({ isDarkModeSupported: false })
)(BlogComponent);
